/**
 * Modelos de autenticação e usuário logado.
 *
 * Derivados do fluxo de login legado:
 * - `app/factories/user.factory.js` (endpoints `POST /auth`, `/auth/cerberus`,
 *   `/auth/user-id`, `/auth/refresh`, `/mobile/auth`)
 * - `app/services/security.service.js` (cookie `loggedUser`)
 * - `app/controllers/login.controller.js` / `app/app.controller.js` (consumo)
 *
 * `startLogin(data)` armazena a resposta completa do login como cookie
 * `loggedUser`; por isso {@link LoggedUser} representa esse envelope
 * `{ auth, user }`, e o usuário em si fica em `loggedUser.user`.
 *
 * Segurança: `token` e `refreshToken` são credenciais — nunca logar, ecoar ou
 * persistir em `localStorage`. Trate-os como opacos.
 */

/** Par de tokens retornado pelo login (`data.auth`). */
export interface Auth {
  token: string;
  refreshToken: string;
}

/**
 * Uma permissão é uma string namespaced (ex.: `"autosync"`,
 * `"autosync:portal"`). No legado, `permissions.includes(root)` opera sobre um
 * array de strings — não são objetos.
 */
export type UserPermission = string;

/**
 * Feature flag associada ao usuário. `root` é uma chave namespaced
 * (ex.: `"display:InternetAddress:VehicleList"`); `isActive` controla o gate.
 * Fonte: `security.service.js` (`flag.root === root`, `flag.isActive`).
 */
export interface FeatureFlag {
  root: string;
  isActive: boolean;
}

/**
 * Chave de uma feature flag de usuário — o `root` de {@link FeatureFlag}.
 *
 * `string` por decisão de design, não por descuido: no legado a flag é um `root`
 * livre e a lista cresce a cada flag criada no backend. **Não transformar em
 * union** — o porquê e as flags reais estão em
 * `docs/spec/16-ARQUITETURA-MIGRACAO.md` §2.16.
 */
export type FeatureFlagKey = string;

/** Dados do usuário autenticado (`loggedUser.user`). */
export interface User {
  id: number;
  /** Identificador da instância/loja. Usado como `instanceId` nas APIs. */
  instanceId: number;
  countryId: number;
  name: string;
  email?: string;
  permissions: UserPermission[];
  featureFlags: FeatureFlag[];
  /** Presente apenas no fluxo Cerberus (`/auth/cerberus`); usado como header `userid`. */
  ego_user_id?: string | number;
}

/**
 * Envelope completo do login, armazenado no cookie `loggedUser`.
 * Corresponde à resposta de `POST /auth`.
 */
export interface LoggedUser {
  auth: Auth;
  user: User;
  /** Estado do login (ex.: erro de status do usuário); presente só no login. */
  state?: { error?: string; code?: number | string };
}

export interface MfaChallenge {
  mfaRequired: true;
  requestId: string;
}

export type SignInResult = LoggedUser | MfaChallenge;

export function isMfaChallenge(result: SignInResult): result is MfaChallenge {
  return (result as MfaChallenge)?.mfaRequired === true;
}
